/*----------------------------------
- NEWSLETTER PAGE CONTENT CATALOG (SSOT)
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { NEWSLETTER_AGENDA_DAYS_AHEAD } from '@uniquedomains/components/contracts/newsletter/public';
import { DEALFLOW_COPY } from '@uniquedomains/components/contracts/opportunities/dealflowCopy';

/*----------------------------------
- TYPES
----------------------------------*/

export type NewsletterSendId = 'news-brief' | 'agenda' | 'opportunities';

export type NewsletterScheduleItem = {
    id: NewsletterSendId;
    tag: string;
    cadence: string;
};

/** One line of an illustrative send preview. Never a live row, never a real figure. */
export type NewsletterPreviewRow = {
    text: string;
    meta: string;
};

export type NewsletterSavingsCard = {
    id: NewsletterSendId;
    tag: string;
    cadence: string;
    headline: string;
    body: string;
    previewLabel: string;
    preview: readonly NewsletterPreviewRow[];
};

export type NewsletterAudience = {
    id: 'domainers' | 'investors' | 'solopreneurs';
    who: string;
    body: string;
};

/**
 * The lines a capture shows after a submit, one per outcome of `useNewsletterSubscribe` that has a line (idle and
 * sending show none). Keyed by the hook's own status names so the page maps them without a translation table.
 */
export type NewsletterOutcomeLines = {
    subscribed: string;
    invalid: string;
    too_fast: string;
    rate_limited: string;
    error: string;
};

/*----------------------------------
- CONTENT
----------------------------------*/

/**
 * Copy notes, kept here because the 2026-09 mockup contradicts decisions the code
 * already carries:
 * - the editions are weekly, so no daily wording ships;
 * - the client may not state a publisher count it cannot read from the backend,
 *   so "Eleven publishers" and "Four hours of reading a week" are not shipped;
 * - the Opportunities email is picked from one engine-ordered pool, so no
 *   sentence claims the rows are scored against a reader's own criteria;
 * - "Ten minutes on Monday, five on Thursday" is a per-send reading time the
 *   backend disagrees with (`readingMinutes` on the published edition currently
 *   reads 4), so the closing band keeps its verified heading;
 * - the sentence "Every claim cited." is retired site-wide by owner decision, so
 *   the citation promise is worded as the link it actually is;
 * - the Wednesday agenda covers `NEWSLETTER_AGENDA_DAYS_AHEAD` days ahead, the
 *   contract constant Product's send window reads.
 *
 * The three `preview` blocks are decoration: illustrative sample lines, not live
 * data. They are rendered `aria-hidden` and they carry no figure a reader could
 * mistake for a published number. The first publisher is the neutral "Trade press"
 * (the website mock of 2026-10-25, the sales-source guard), the two others are real
 * pipeline sources (`apps/product/server/catalogs/newsSources.ts`), the sample
 * domains are not real listings, and the agenda lines name no real TLD or date,
 * which would read as a live launch and go stale.
 *
 * "Brief" is never a noun in copy (the website copy rule, 2026-10-25): the Monday
 * email is "the weekly news", the meta description included (Gaetan, 2026-10-27: a copy
 * rule fix reaches an SEO surface through this catalog, decision 6A). The send id
 * `news-brief` keeps its name.
 */
export const NEWSLETTER_PAGE_CONTENT = {
    meta: {
        pageTitle: 'Domaining Newsletter: the week in domains, every Monday',
        metaDescription:
            'Three weekly emails: the domaining weekly news on Monday at 07:00 UTC, the TLD and events agenda on Wednesday, and one-word domain opportunities on Thursday.',
    },
    hero: {
        headline: 'Read the domain market in ten minutes a week',
        lede:
            'Three short emails: what the industry reported, with every claim linked to its source, the launch dates and events ahead, and a handful of one-word names worth a look.',
        reassurance: 'Free, no account needed. One click to leave, from the footer of any email.',
        submitLabel: 'Subscribe',
    },
    schedule: {
        label: 'Three emails, never more',
        items: [
            { id: 'news-brief', tag: 'Weekly news', cadence: 'Weekly, Mondays' },
            { id: 'agenda', tag: 'Agenda', cadence: 'Weekly, Wednesdays' },
            { id: 'opportunities', tag: DEALFLOW_COPY.investor.label, cadence: 'Weekly, Thursdays' },
        ],
        caption: 'Monday, Wednesday and Thursday. Nothing outside those three sends.',
    },
    savings: {
        heading: 'What it saves you',
        support: 'One read on Monday, the dates ahead on Wednesday, one shortlist on Thursday.',
        cards: [
            {
                id: 'news-brief',
                tag: 'Weekly news',
                cadence: 'Weekly, Mondays',
                headline: 'One read instead of twelve tabs',
                body:
                    'What the domain industry reported that week, in one summary: sales, auctions, policy, legal, registries. Each claim links to the article it came from.',
                previewLabel: 'Sample weekly news',
                preview: [
                    // Never a source of the sales archive nor the publication it stores as a sale's
                    // reporter (ADR-0067; the sales-source guard,
                    // docs/fixes/2026-10-25-news-names-a-guarded-sales-source.md). The first row names no
                    // publication at all, as the mock draws it: a typed name here is what the guard caught.
                    { text: 'Registry and policy moves', meta: 'Trade press' },
                    { text: 'One-word sale prices', meta: 'Domain Name Wire' },
                    { text: 'UDRP decisions', meta: 'ICA UDRP Digest' },
                ],
            },
            {
                id: 'agenda',
                tag: 'Agenda',
                cadence: 'Weekly, Wednesdays',
                headline: 'Launch dates before they pass',
                body:
                    `TLD launch phases, registry changes and domain industry events for the next ${NEWSLETTER_AGENDA_DAYS_AHEAD} days, from the Unique Domains agenda. The whole agenda is also a calendar feed you can subscribe to.`,
                previewLabel: 'Sample Wednesday agenda',
                preview: [
                    { text: 'Sunrise opens for a new TLD', meta: 'Launch' },
                    { text: 'A TLD enters the root zone', meta: 'Registry' },
                    { text: 'A domain industry conference', meta: 'Event' },
                ],
            },
            {
                id: 'opportunities',
                tag: DEALFLOW_COPY.investor.label,
                cadence: 'Weekly, Thursdays',
                headline: 'One-word names worth a look',
                body:
                    // Founders get this send under the founder name; the page uses the investor one
                    // because a subscriber's persona is unknown here (ADR-0074).
                    `Five one-word domains from that day's ${DEALFLOW_COPY.investor.label} edition, each with its status, ask price and a business idea, linking the edition. The list itself refreshes every day at 06:00 UTC. Founders receive it as ${DEALFLOW_COPY.founder.label}. Verify before you buy.`,
                previewLabel: 'Sample Thursday list',
                preview: [
                    { text: 'gridshift.ai', meta: '$2,480' },
                    { text: 'drowsy.co', meta: '$890' },
                    { text: 'clipper.io', meta: '$4,200' },
                ],
            },
        ],
    },
    audiences: {
        heading: 'Written for the people it is about',
        items: [
            {
                id: 'domainers',
                who: 'Domainers',
                body: 'Know which categories are selling at asking price before you reprice the portfolio.',
            },
            {
                id: 'investors',
                who: 'Domain investors',
                body: 'Follow where the money lands: price bands, escrow volume, contested names.',
            },
            {
                id: 'solopreneurs',
                who: 'Serial solopreneurs',
                body: 'Catch the one-word name for the next project the week it frees up.',
            },
        ],
    },
    ctaBand: {
        heading: "Start with Monday's weekly news",
        body: 'Every published edition stays online, with its sources.',
        submitLabel: 'Subscribe',
    },
    archiveLink: {
        label: 'Read the latest editions',
    },
    /** Both captures' field, decoy and outcome lines (the shipped `NewsletterSubscribeForm` wording). */
    form: {
        fieldLabel: 'Email address',
        placeholder: 'you@company.com',
        // The decoy's label avoids every token a password manager fills: an autofill match would drop a real subscriber.
        decoyLabel: 'Leave this field empty',
        outcomes: {
            // One confirmation for every accepted address: the endpoint never says whether it was already on the list.
            subscribed: 'You are on the list. The weekly news lands Monday at 07:00 UTC.',
            invalid: 'Enter a valid email address.',
            too_fast: 'That went through a little too quickly. Submit again to confirm.',
            rate_limited: 'Too many attempts. Try again in a minute.',
            error: 'Subscription is temporarily unavailable. Please try again shortly.',
        },
    },
    related: {
        label: 'Related pages',
    },
} as const satisfies {
    meta: { pageTitle: string; metaDescription: string };
    hero: {
        headline: string;
        lede: string;
        reassurance: string;
        submitLabel: string;
    };
    schedule: { label: string; items: readonly NewsletterScheduleItem[]; caption: string };
    savings: { heading: string; support: string; cards: readonly NewsletterSavingsCard[] };
    audiences: { heading: string; items: readonly NewsletterAudience[] };
    ctaBand: { heading: string; body: string; submitLabel: string };
    archiveLink: { label: string };
    form: { fieldLabel: string; placeholder: string; decoyLabel: string; outcomes: NewsletterOutcomeLines };
    related: { label: string };
};
